// src/components/desk/ParseConflictPanel.jsx — one row per field a parse disagrees with.
//
// Owner: "user must be asked do you want to replace this with this? yes or no. IF THERE ARE MANY
// ASK ON EACH ONE." That last clause is the whole shape of this component: a single "the parse
// changed 6 things, accept?" would be the silent overwrite with one extra click in front of it.
//
// ⚠️ ITS OWN FILE so it can be RENDERED. It was written inline in ManualPropertyModal, which is
// ~14,000 lines, mounts a map and a gallery, and needs a login — so the panel could not be drawn
// by anything, and "one row per field" was a claim about code I had read rather than about
// markup anyone had seen. Everything else about it is unchanged.
//
// ⚠️ DEFAULT IS KEEP. `answers` starts empty and only an explicit tick counts, so dismissing the
// panel, closing the modal, or pressing the primary button without reading all leave every value
// alone. A confirmation that approves by default is the silent overwrite wearing a button.
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn } from './primitives.jsx'

export function ParseConflictPanel({ conflicts, answers, onToggle, onApply, onKeepAll, cardClass = '' }) {
  if (!conflicts?.length) return null
  const chosenCount = Object.values(answers || {}).filter(Boolean).length
  return (
    <div className={`${cardClass} border-amber-500/40 bg-amber-500/5`}>
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">
        <Icon name="alert-triangle" size={13} />{' '}
        {tLog('desk_parse_conflict_heading', { n: String(conflicts.length) })}
      </p>
      <div className="space-y-1.5">
        {conflicts.map(c => {
          const chosen = answers?.[c.key] === true
          return (
            <div key={c.key} data-conflict-row={c.key}
              className="flex flex-wrap items-center gap-2 text-xs">
              {/* ⚠️ The LABEL truncates, never the values. Same rule as StateRow on the card
                  face: an agent cannot answer "replace 1200 with what?", and a clipped number
                  is how a wrong price gets approved. */}
              <span className="font-medium min-w-0 truncate">{c.label}</span>
              <span className="tabular-nums text-muted-foreground line-through">{String(c.from)}</span>
              <span className="text-muted-foreground">→</span>
              <span className="tabular-nums font-medium">{String(c.to)}</span>
              <span className="grow" />
              <button type="button" aria-pressed={chosen}
                className={`rounded-lg border px-2 py-0.5 ${chosen
                  ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold'
                  : 'border-border hover:bg-accent'}`}
                onClick={() => onToggle(c.key, !chosen)}>
                {t('desk_parse_conflict_replace')}
              </button>
            </div>
          )
        })}
      </div>
      <div className="flex items-center gap-2 mt-2.5">
        {/* The count is on the button, so "Replace 0" is what an untouched panel offers — the
            button states what it will do rather than implying it will accept everything. */}
        <button type="button" className={btn('primary')} onClick={onApply}>
          {tLog('desk_parse_conflict_apply', { n: String(chosenCount) })}
        </button>
        <button type="button" className={btn('ghost')} onClick={onKeepAll}>
          {t('desk_parse_conflict_keep_all')}
        </button>
      </div>
    </div>
  )
}
